import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ReduceCircleLine from '@/assets/images/icons/reduce-circle-line.react.svg?react';
import { BASE_NAME, toSearchStr } from '@/infrastructure/history';
import { isLinux, isElectron, isWeb } from '@/infrastructure/product-info';
import { DATE_TIME_FORMAT } from '@/infrastructure/utils/timestamp';
import { MatchMode } from '@/presentation/components/form/standards/types';
import FreeProductGuide, { Display, GuideType } from '@/presentation/components/free-product-guide';
import { hintChildren } from '@/presentation/components/hint-text/hintText';
import styles from '@/presentation/components/issue-track-icon/index.module.scss';
import { useStores } from '@/presentation/store';
import { CommonFieldKey, StandardCommonFieldKey } from '@/proto/field';
import { IFilter, TimeType } from '@/proto/metric';
import { genStandardToken } from '@/proto/token';

interface IProps {
  eventTime: string;
  reportTime: string;
  issueId?: string | null;
  launchId?: string | null;
  processLaunchId?: string | null;
  accountId?: string | null;
  qimei?: string | null;
}
const toUnix = (time: string) => {
  if (time && moment(time, DATE_TIME_FORMAT).isValid()) {
    return moment(time, DATE_TIME_FORMAT).unix();
  }
  return moment().unix();
};

const collectIdFilters = (
  fields: Array<[string | null | undefined, CommonFieldKey, StandardCommonFieldKey]>,
  filters: IFilter[],
  values: Record<string, any>,
) => {
  fields.forEach(([val, field, standard]) => {
    if (val) {
      filters.push({ name: field, values: [val], operator: MatchMode.OR });
      values[standard] = { values: [val], mode: MatchMode.OR };
    }
  });
};

const openTrackUrl = async (
  startTime: ReturnType<typeof moment>,
  endTime: ReturnType<typeof moment>,
  filters: IFilter[],
  values: Record<string, any>,
) => {
  const token = await genStandardToken({
    date_type: 'event_time',
    short_cut: 'custom',
    time_window: TimeType.DAY,
    time_window_type: TimeType.DAY,
    date_value: [startTime.unix(), endTime.unix()],
    standard_time_range: {
      date_type: 'event_time',
      time_window_type: TimeType.DAY,
      short_cut: 'custom',
      date_value: [
        startTime.format(DATE_TIME_FORMAT),
        endTime.format(DATE_TIME_FORMAT),
      ],
    },
    ...values,
    filters,
  });
  const str = toSearchStr({ token });
  const analysisUrl = (isElectron() || isWeb()) ? '/electron/log' : '/track';
  const a = document.createElement('a');
  a.href = `${location.origin}${BASE_NAME}${analysisUrl}?${str}`;
  a.target = '_blank';
  a.click();
};

export const IssueTrackIcon = (props: IProps) => {
  const { t } = useTranslation();
  const { isFreeProduct } = useStores().ProductStore;
  const isFree = isFreeProduct();
  const { eventTime, reportTime, launchId, processLaunchId, accountId, qimei } = props;
  const { pathname } = useLocation();
  if ((!launchId && !processLaunchId && !accountId && !qimei) || pathname === '/track' || isLinux()) {
    return null;
  }
  const click = async () => {
    const eventTimeUnix = toUnix(eventTime);
    const reportTimeUnix = toUnix(reportTime);
    const minTime = Math.min(eventTimeUnix, reportTimeUnix);
    const maxTime = Math.max(eventTimeUnix, reportTimeUnix);
    const startTime = moment.unix(minTime).subtract(10, 'minutes');
    const endTime = moment.unix(maxTime).add(10, 'minutes');
    const filters: IFilter[] = [];
    const values: Record<string, any> = {};
    collectIdFilters(
      [
        [launchId, CommonFieldKey.LaunchId, StandardCommonFieldKey.LaunchId],
        [processLaunchId, CommonFieldKey.ProcessLaunchId, StandardCommonFieldKey.ProcessLaunchId],
        [accountId, CommonFieldKey.AccountId, StandardCommonFieldKey.AccountId],
        [qimei, CommonFieldKey.Qimei, StandardCommonFieldKey.Qimei],
      ],
      filters,
      values,
    );
    filters.push(
      { name: 'event_time', values: [startTime.unix().toString()], operator: 'GE' },
      { name: 'event_time', values: [endTime.unix().toString()], operator: 'LE' },
    );
    await openTrackUrl(startTime, endTime, filters, values);
  };
  return <FreeProductGuide guideType={GuideType.POP_CONFIRM} display={Display.BLOCK}>
    <div style={isFree ? { color: '#d9d9d9' } : undefined} className={styles.icon} onClick={click}>
      {isFree ? <ReduceCircleLine /> : hintChildren(<ReduceCircleLine />, t('追踪'))}
    </div>
  </FreeProductGuide>;
};
